import type { FilterSchema } from '@/shared/components/filter/validation'
import type { SRT_SortingState } from '@/shared/components/master-table'
import type {
	CategoryModel,
	CPIResponseModel,
	OptimizationIDModel,
	StatisticsInterpretabilityGroupedResponseModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import React, { useMemo, useState } from 'react'
import {
	isCompetitorFilter,
	isProductFilter,
} from '@/pages/dashboard/lib/filters'
import { keyBy, last, uniqBy } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useCPIQuery } from '@/entities/bi'
import { useCategoriesQuery } from '@/entities/core'
import { useSelectedCurrency } from '@/entities/currency'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import {
	createSRTColumnHelper,
	MasterTable,
	useMasterReactTable,
} from '@/shared/components/master-table'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
} from '@/shared/components/ui/empty'
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { cn } from '@/shared/lib/utils'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'
import { toPercent } from '@/shared/tools/utils'

import Widget from '../../Widget/Widget'
import WidgetContent from '../../Widget/WidgetContent'
import WidgetHeader from '../../Widget/WidgetHeader'

export default function SubcategoryPerformanceWidget({
	commonFilters,
	className,
}: {
	commonFilters: FilterSchema[]
	className?: string
}) {
	const { t } = useTranslation()

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.subcategory_performance.title')}
				tooltip={t('widget.subcategory_performance.tooltip')}
				subtitle={t('widget.subcategory_performance.description')}
			/>
			<WidgetContent className='truncate px-0'>
				<ErrorBoundary fallback={<ErrorFallback />}>
					<SubcategoryPerformanceTable commonFilters={commonFilters} />
				</ErrorBoundary>
			</WidgetContent>
		</Widget>
	)
}

const SubcategoryPerformanceTable = ({
	commonFilters,
}: {
	commonFilters: FilterSchema[]
}) => {
	const { t } = useTranslation()
	const optimizationsQuery = useOptimizationsQuery<OptimizationIDModel[]>({
		select: getFinishedAndLockedOptimizations,
	})

	const currency = useSelectedCurrency()
	const statisticsQuery = useInterpretabilityStatisticQuery(
		{
			currency,
			optimizations: optimizationsQuery.data || [],
			filters: commonFilters.filter(isProductFilter) || [],
			dimensions: ['category_ids' as const],
			label: 'SubcategoryPerformance',
		},
		{
			enabled: !!optimizationsQuery.data,
		}
	)
	const cpiQuery = useCPIQuery({
		product_filters: commonFilters.filter(isProductFilter) || [],
		cd_filters: commonFilters.filter(isCompetitorFilter) || [],
		dimensions: ['category_ids' as const],
		label: 'SubcategoryPerformance',
	})
	const categoriesQuery = useCategoriesQuery({})
	const rows = useMemo(() => {
		if (!categoriesQuery.data || !statisticsQuery.data || !cpiQuery.data)
			return []
		const statisticsMap = keyBy(
			'category_id',
			sanitizeStatistics(
				statisticsQuery.data as StatisticsInterpretabilityGroupedResponseModel[]
			)
		)
		const cpiMap = keyBy('category_id', sanitizeCPI(cpiQuery.data))

		return sanitizeCategories(categoriesQuery.data)
			.filter(
				(category) => category.id in statisticsMap || category.id in cpiMap
			)
			?.map((category) => ({
				category_id: category.id,
				category_name: category.name,
				revenue_impact: statisticsMap[category.id]?.revenue_impact || undefined,
				gross_profit_impact: statisticsMap[category.id]?.gross_profit_impact,
				gross_margin: statisticsMap[category.id]?.gross_margin,
				gross_profit_margin_diff:
					statisticsMap[category.id]?.gross_profit_margin_diff,
				cpi: cpiMap[category.id]?.cpi,
			}))
	}, [categoriesQuery.data, statisticsQuery.data, cpiQuery.data])

	const columns = useMemo(() => getColumns(t, currency), [t, currency])
	const [sorting, setSorting] = useState<SRT_SortingState>([
		{
			id: 'revenue_impact',
			desc: true,
		},
	])
	const isLoading =
		optimizationsQuery.isLoading ||
		statisticsQuery.isLoading ||
		cpiQuery.isLoading ||
		categoriesQuery.isLoading

	const isError =
		optimizationsQuery.isError ||
		statisticsQuery.isError ||
		cpiQuery.isError ||
		categoriesQuery.isError

	const table = useMasterReactTable({
		columns,
		data: rows,
		layoutMode: 'grid',
		sortUndefined: 'first',
		state: {
			sorting,
			isLoading,
		},
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},

		enableColumnActions: false,
		enableColumnResizing: false,
		onSortingChange: setSorting,
	})
	if (isError) {
		return <ErrorFallback />
	}

	return (
		<div className='border-base relative flex max-h-[217px] flex-1 flex-col overflow-hidden border-y border-solid'>
			<MasterTable table={table} />
		</div>
	)
}
const ErrorFallback = () => {
	const { t } = useTranslation()

	return (
		<Empty className='md:px-4'>
			<EmptyHeader>
				<EmptyMedia
					variant='icon'
					className='bg-destructive/20 text-destructive'
				>
					<AlertTriangleIcon />
				</EmptyMedia>
				<EmptyDescription className='text-destructive'>
					{t('error.unrecoverable.title')}
				</EmptyDescription>
			</EmptyHeader>
		</Empty>
	)
}
const sanitizeCPI = (data: CPIResponseModel[]) =>
	data.map((item) => ({
		category_id: last(item.category_ids),
		cpi: item.cpi,
	}))

const calculateGrossProfitMarginDiff = (
	final: number | undefined,
	init: number | undefined
) => {
	if (final === undefined || init === undefined) return undefined

	return (final - init) * 100
}
const sanitizeStatistics = (
	data: StatisticsInterpretabilityGroupedResponseModel[]
) =>
	data.map((item) => ({
		category_id: last(item.category_ids),
		revenue_impact: item.metrics['predict_revenue.final'],
		gross_profit_impact: item.metrics['predict_gross_profit.final'],
		gross_margin: toPercent(item.metrics['predict_gross_profit_margin.final']),
		gross_profit_margin_diff: calculateGrossProfitMarginDiff(
			item.metrics['predict_gross_profit_margin.final'],
			item.metrics['predict_gross_profit_margin.init']
		),
	}))
const sanitizeCategories = (categories: CategoryModel[]) => {
	const parents: string[] = []

	for (const category of categories) {
		if (category.parent_id) {
			parents.push(category.parent_id)
		}
	}

	return uniqBy(
		'id',
		categories.filter((category) => !parents.includes(category.id))
	)
}
type SubcategoryPerfomanceRow = {
	category_id: string
	category_name: string
	revenue_impact?: number
	gross_profit_impact?: number
	gross_margin?: number
	gross_profit_margin_diff?: number
	cpi?: number
}
const columnHelper = createSRTColumnHelper<SubcategoryPerfomanceRow>()
const getColumns = (t: TFunction, currency?: string) => [
	columnHelper.accessor('category_name', {
		id: 'category_name',
		header: t('field_schema_category', {
			ns: 'fields',
			defaultValue: 'Category',
		}),
		enableSorting: true,
		Cell: ({ cellValue }) => (
			<Tooltip>
				<TooltipTrigger asChild>
					<div className='truncate text-xs'>
						{cellValue === null || cellValue === undefined ? '-' : cellValue}
					</div>
				</TooltipTrigger>
				<TooltipContent>{cellValue}</TooltipContent>
			</Tooltip>
		),
	}),
	columnHelper.accessor('revenue_impact', {
		id: 'revenue_impact',
		header: t('col_revenue_impact_short', {
			defaultValue: 'REV IMP',
		}),
		description: t('col_revenue_impact_short.description'),
		Cell: ({ cellValue }) => (
			<div className='cell-data-number text-xs'>
				{cellValue === null || cellValue === undefined
					? '-'
					: formatMoney(cellValue, currency, NumberFormats.Compact)}
			</div>
		),
		size: 90,
		sortUndefined: 'last',
	}),
	columnHelper.accessor('gross_profit_impact', {
		id: 'gross_profit_impact',
		header: t('col_gross_profit_impact_short', {
			defaultValue: 'GP IMP',
		}),
		description: t('col_gross_profit_impact_short.description'),
		Cell: ({ cellValue }) => (
			<div className='cell-data-number text-xs'>
				{cellValue === null || cellValue === undefined
					? '-'
					: formatMoney(cellValue, currency, NumberFormats.Compact)}
			</div>
		),
		size: 90,
	}),
	columnHelper.accessor('gross_margin', {
		id: 'gross_margin',
		header: t('col_gross_margin_short', {
			defaultValue: 'GM',
		}),
		description: t('col_gross_margin_short.description'),
		Cell: ({ cellValue }) => (
			<div className='cell-data-number text-xs'>
				{cellValue === null || cellValue === undefined
					? '-'
					: formatNumber(cellValue, NumberFormats.Percent)}
			</div>
		),
		size: 90,
	}),
	columnHelper.accessor('gross_profit_margin_diff', {
		id: 'gross_profit_margin_diff',
		header: t('col_vs_curent_short', {
			defaultValue: 'VS CUR',
		}),
		description: t('col_vs_curent_short.description'),
		Cell: ({ cellValue }) => (
			<div className='cell-data-number text-xs'>
				{cellValue === null || cellValue === undefined
					? '-'
					: `${formatNumber(cellValue, NumberFormats.Fixed)}${t('pp')}`}
			</div>
		),
		size: 90,
	}),
	columnHelper.accessor('cpi', {
		id: 'cpi',
		header: t('col_cpi_short', {
			defaultValue: 'CPI',
		}),
		description: t('col_cpi_short.description'),
		Cell: ({ cellValue }) => (
			<div className='cell-data-number text-xs'>
				{cellValue === null || cellValue === undefined
					? '-'
					: formatNumber(cellValue, NumberFormats.Fixed)}
			</div>
		),
		size: 90,
	}),
]
